import styled, { css } from 'styled-components/macro';
import { Link as RouterLink } from 'react-router-dom';

import Box from 'components/elements/Box';
import Span from 'components/elements/span';
import Table from 'components/data-visualization/Table/Table';
import {
  Header,
  HeaderButton,
} from 'components/data-visualization/Table/DropdownHead.style';
import Row from 'components/data-visualization/Table/Row';
import Cell from 'components/data-visualization/Table/Cell';
import { SearchFieldContainer } from 'components/styled/SearchField/SearchField';
import Layout from 'components/styled/Layout/Layout';
import { TablePlaceholder } from 'components/data-visualization/Table/Placeholder';
import { FooterBar } from 'components/Overview/Container/shared.style';
import P from 'components/elements/p';
import { ReactComponent as InformationIcon } from 'icons/icon-info.svg';

import { definitionLabel } from 'styles/base-classes';
import { ButtonBox, StyledDropdown } from 'components/base/DSPFilter/styles';

export const WrapperBox = styled('section')`
  margin: 0.5rem 0.5rem 0;
  padding: 1.3rem 1.8rem 0.7rem;
  background-color: ${({ theme }): string => theme.White};

  @media screen and (min-width: 40rem) {
    margin: 0.5rem 0.5rem 0;
  }
  @media screen and (min-width: 50rem) {
    width: 90%;
    margin: 16px auto 0;
  }
  @media screen and (min-width: 80rem) {
    width: 75%;
  }
`;

export const BackLinkBox = styled(Box)`
  margin: 0.5em 0.5em -0.5em;
  width: calc(100% - 2 * 0.5em);
  padding: 1.5em 1.5em 0.5em;

  @media screen and (min-width: 40rem) {
    padding: 2em 2em 0;
  }
  @media screen and (min-width: 50rem) {
    width: 90%;
    margin: 1em auto -1em;
  }
  @media screen and (min-width: 80rem) {
    width: 75%;
  }
`;

export const IconBox = styled(Box)<{
  isForwardTo?: boolean;
  isBackTo?: boolean;
}>`
  ${({ isForwardTo }): any =>
    isForwardTo &&
    css`
      margin-left: 10px;
      margin-top: 3px;
    `}

  ${({ isBackTo }): any =>
    isBackTo &&
    css`
      svg {
        width: 6px;
        height: 17px;
        path {
          stroke-width: 2px;
        }
      }
    `}
`;

export const Link = styled(RouterLink)`
  display: flex;
  align-items: center;

  &:active {
    color: ${({ theme }): string => theme.DeepBlue};
  }

  &.disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.5;
  }

  &.move-to-right {
    &:hover svg,
    &:focus svg {
      transform: translateX(0.25em);
    }
  }

  &.move-to-left {
    &:hover svg,
    &:focus svg {
      transform: translateX(-0.25em);
    }
  }

  &:hover svg,
  &:focus svg {
    transition: transform ${({ theme }): string => theme.transitionDuration};
    transition-timing-function: ${({ theme }): string => theme.timingFunction};
  }
`;

export const LinkSpan = styled(Span)`
  padding-bottom: 1px;
  box-shadow: 0px 1px 0px 0px ${({ theme }): string => theme.AccentGreen};
  color: ${({ theme }): string => theme.DeepBlue};

  &:hover {
    box-shadow: 0px 2px 0px 0px ${({ theme }): string => theme.AccentGreen};
  }
`;

export const StyledTable = styled(Table)`
  ${Header} {
    padding-left: 0;
    width: 17.5%;

    &:first-of-type {
      width: 40%;
    }
    &:last-of-type {
      width: 25%;
    }
  }
  ${Row} ${Cell}:last-of-type {
    padding-right: 0.5rem;
  }
  ${HeaderButton} {
    text-align: center;
  }
`;

export const Label = styled(Span)`
  ${definitionLabel};
  display: flex;
  align-items: center;
  padding-top: 2px;
  flex-basis: 35%;
  color: ${({ theme }): string => theme.MediumGrey};
  white-space: nowrap;
`;

export const SearchWrapperBox = styled(Box)`
  ${SearchFieldContainer} {
    max-height: 43px;
    margin-bottom: 0.625rem;
  }
`;

export const GenericBox = styled(Box)`
  ${Layout.MaxWidth} {
    @media screen and (max-width: 640px) {
      max-width: calc(100% - (1.625rem * 2));
    }
  }
`;

export const PlaceholderBox = styled(Box)`
  ${TablePlaceholder} {
    min-height: 12em;
  }
`;

export const FooterLinkBox = styled(FooterBar)<{ isLinkActive?: boolean }>`
  padding: 1em 0;

  a {
    pointer-events: none;
    cursor: default;
    opacity: 0.5;
  }

  ${({ isLinkActive }): any =>
    isLinkActive &&
    css`
      a {
        pointer-events: initial;
        cursor: pointer;
        opacity: 1;
      }
    `}
`;

export const H2 = styled('h2')<{ isLargeScreen?: boolean }>`
  margin-bottom: 0;
  margin-top: 1rem;
  font-size: ${({ isLargeScreen }): string =>
    isLargeScreen ? '1.5rem' : '1.25rem'};
  font-weight: 700;
`;

export const TextWithLink = styled(P)`
  a {
    padding: 0;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
    border-bottom: 2px solid ${({ theme }): string => theme.AccentGreen};
    line-height: 1.2;
    white-space: nowrap;

    &:hover {
      border-width: 3px;
    }
  }
  svg {
    display: none;
  }
`;

export const TableCell = styled(Cell)<{ textAlign?: string }>`
  text-align: ${({ textAlign = 'left' }): string => textAlign};
`;

export const TableRow = styled(Row)`
  color: ${({ theme }): string => theme.ShadowBlack};
  font-size: 0.875rem;
  &:not(:last-child) ${TableCell} {
    border-bottom: 1px solid #e8e8e8;
  }
`;

export const Reference = styled(RouterLink)`
  border-bottom: 1px dashed ${({ theme }): string => theme.MediumGrey};
  font-size: 15px;
  color: ${({ theme }): string => theme.ShadowBlack};
`;

export const InfoIcon = styled(InformationIcon)`
  width: 0.875rem;
  height: 0.875rem;
  cursor: pointer;

  circle,
  path {
    stroke: ${({ theme }): string => theme.DeepBlue};
  }
`;

export const DspFilterContainer = styled(Box)`
  position: relative;
  @media screen and (min-width: 62em) {
    margin-left: 1rem;
  }

  ${StyledDropdown} {
    width: 268px;
    height: 46px;
    background: ${({ theme }): string => theme.Sand};
    border-left: none;
    line-height: 38px;

    &:hover {
      background: ${({ theme }): string => theme.LightGrey};
    }

    figure {
      margin-left: 0;
      top: auto;
    }

    ${ButtonBox} {
      padding: 0.25rem 0.75rem;
    }

    @media screen and (max-width: 62em) {
      width: 100%;
      margin-left: 0;

      figure {
        margin-left: 0;
        top: auto;
        width: 100%;
        left: 0;
      }
    }
  }
`;
